import { ticketTypeLabel } from '../../pricing/pure/perConcert.ts'
import { type DoorBalance } from './door-balance.ts'

/** How one ticket stands at the door, in the order the door cares about. */
export type DoorTicketState = 'refunded' | 'used' | 'door' | 'unpaid' | 'valid'

/** Below this, a ticket's balance counts as settled (cash rounding). */
const EPSILON = 0.1

/** Still owes money — an unknown amount (no prices yet) is not paid either. */
const owesAtDoor = (balance: DoorBalance) =>
	!balance.refunded &&
	(balance.outstanding == null || balance.outstanding > EPSILON)

/**
 * One ticket's state at the door: a refunded ticket is void, a used one has
 * been let in (whatever it still owes is tracked separately), one that owes
 * is paid at the door — DOOR as planned, or not yet paid online — and the
 * rest are valid.
 */
export function doorTicketState(ticket: {
	used: boolean
	settlement: string
	balance: DoorBalance
}): DoorTicketState {
	if (ticket.balance.refunded) return 'refunded'
	if (ticket.used) return 'used'
	if (owesAtDoor(ticket.balance)) {
		return ticket.settlement === 'DOOR' ? 'door' : 'unpaid'
	}
	return 'valid'
}

type DoorPassSummary = {
	/** The party as a whole: refunded when nothing is left to let in, used
	 * once everyone is in, else what is still owed — or valid */
	state: DoorTicketState
	/** Owed over the party's tickets; `null` while a price is unknown */
	outstanding: number | null
	/** Tickets that still count (not refunded) */
	count: number
	/** Of those, already let in */
	used: number
}

/**
 * A door pass covers all of one buyer's tickets to a concert: the door sees
 * one state for the party, plus what is still owed across it.
 */
export function doorPassSummary(
	tickets: ReadonlyArray<{
		used: boolean
		settlement: string
		balance: DoorBalance
	}>,
): DoorPassSummary {
	const live = tickets.filter((ticket) => !ticket.balance.refunded)
	const owing = live.filter((ticket) => owesAtDoor(ticket.balance))
	const outstanding = owing.some((ticket) => ticket.balance.outstanding == null)
		? null
		: Math.round(
				owing.reduce((sum, ticket) => sum + ticket.balance.outstanding!, 0) *
					100,
			) / 100
	const used = live.filter((ticket) => ticket.used).length
	const state: DoorTicketState =
		live.length === 0
			? 'refunded'
			: used === live.length
				? 'used'
				: owing.length === 0
					? 'valid'
					: owing.every((ticket) => ticket.settlement === 'DOOR')
						? 'door'
						: 'unpaid'
	return {
		state,
		outstanding: owing.length === 0 ? 0 : outstanding,
		count: live.length,
		used,
	}
}

/** "Kari", or "Ordinær 2" when there are several unnamed tickets of a type */
export function ticketLabel<T extends { name: string | null; type: string }>(
	ticket: T,
	all: ReadonlyArray<T>,
): string {
	if (ticket.name) return ticket.name
	const type = ticketTypeLabel(ticket.type)
	const sameType = all.filter((t) => !t.name && t.type === ticket.type)
	return sameType.length > 1 ? `${type} ${sameType.indexOf(ticket) + 1}` : type
}

/** "1 billett", "3 billettar" */
export const ticketCount = (count: number) =>
	`${count} ${count === 1 ? 'billett' : 'billettar'}`
